import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { FormSection } from '@/shared/ui/components/form/FormSectionCollapsible';
import { Label, TextInput } from '@/shared/ui';
import { FORM_FIELD_LIMITS } from '@/shared/utils/form-field-limits';
import { onlyDigits } from '../../utils/masks';
import { FieldError } from './FieldError';
import { labelStyle } from './drawer-styles';
import type { ClientFormData } from './schema';

interface AddressSectionProps {
  form: UseFormReturn<ClientFormData>;
  collapsed: boolean;
  onToggle: () => void;
}

/**
 * Applies a Brazilian CEP postal code mask (XXXXX-XXX).
 */
function maskCEP(value: string): string {
  const digits = onlyDigits(value).slice(0, 8);
  return digits.replace(/(\d{5})(\d{1,3})/, '$1-$2');
}

interface ViaCepResponse {
  cep: string;
  logradouro: string;
  complemento: string;
  bairro: string;
  localidade: string;
  uf: string;
  erro?: boolean;
}

/**
 * Address fields section with auto-fill from ViaCEP.
 */
export const AddressSection: React.FC<AddressSectionProps> = ({ form, collapsed, onToggle }) => {
  const { t } = useTranslation('features/clients');

  const fetchAddress = useCallback(async (cep: string) => {
    const digits = onlyDigits(cep);
    if (digits.length !== 8) return;

    try {
      const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      if (!res.ok) return;
      const data: ViaCepResponse = await res.json();
      if (data.erro) return;

      form.setValue('address.street', data.logradouro || '', { shouldDirty: true });
      form.setValue('address.neighborhood', data.bairro || '', { shouldDirty: true });
      form.setValue('address.city', data.localidade || '', { shouldDirty: true });
      form.setValue('address.state', data.uf || '', { shouldDirty: true });
      if (data.complemento) {
        form.setValue('address.complement', data.complemento, { shouldDirty: true });
      }
    } catch {
      /* network error — user fills manually */
    }
  }, [form]);

  return (
    <FormSection
      title={t('drawer.address.title')}
      variant="quiet"
      divider
      collapsed={collapsed}
      onToggle={onToggle}
    >
      <Controller
        name="address.zipCode"
        control={form.control}
        render={({ field, fieldState }) => (
          <div className="flex flex-col">
            <Label style={labelStyle}>{t('drawer.address.zipCode')}</Label>
            <TextInput
              {...field}
              value={field.value ?? ''}
              placeholder="00000-000"
              onChange={(e) => {
                const masked = maskCEP(e.target.value);
                field.onChange(masked);
                if (onlyDigits(masked).length === 8) {
                  fetchAddress(masked);
                }
              }}
              maxLength={9}
            />
            <FieldError message={fieldState.error?.message} />
          </div>
        )}
      />

      <div className="grid grid-cols-[1fr_5rem] gap-(--space-2)">
        <Controller
          name="address.street"
          control={form.control}
          render={({ field, fieldState }) => (
            <div className="flex flex-col">
              <Label style={labelStyle}>{t('drawer.address.street')}</Label>
              <TextInput
                {...field}
                value={field.value ?? ''}
                placeholder={t('drawer.address.streetPlaceholder')}
                maxLength={FORM_FIELD_LIMITS.street}
              />
              <FieldError message={fieldState.error?.message} />
            </div>
          )}
        />
        <Controller
          name="address.number"
          control={form.control}
          render={({ field, fieldState }) => (
            <div className="flex flex-col">
              <Label style={labelStyle}>{t('drawer.address.number')}</Label>
              <TextInput {...field} value={field.value ?? ''} placeholder="Nº" maxLength={FORM_FIELD_LIMITS.number} />
              <FieldError message={fieldState.error?.message} />
            </div>
          )}
        />
      </div>

      <Controller
        name="address.complement"
        control={form.control}
        render={({ field, fieldState }) => (
          <div className="flex flex-col">
            <Label style={labelStyle}>{t('drawer.address.complement')}</Label>
            <TextInput
              {...field}
              value={field.value ?? ''}
              placeholder={t('drawer.address.complementPlaceholder')}
              maxLength={FORM_FIELD_LIMITS.complement}
            />
            <FieldError message={fieldState.error?.message} />
          </div>
        )}
      />

      <Controller
        name="address.neighborhood"
        control={form.control}
        render={({ field, fieldState }) => (
          <div className="flex flex-col">
            <Label style={labelStyle}>{t('drawer.address.neighborhood')}</Label>
            <TextInput
              {...field}
              value={field.value ?? ''}
              placeholder={t('drawer.address.neighborhoodPlaceholder')}
              maxLength={FORM_FIELD_LIMITS.neighborhood}
            />
            <FieldError message={fieldState.error?.message} />
          </div>
        )}
      />

      <div className="grid grid-cols-[1fr_5rem] gap-(--space-2)">
        <Controller
          name="address.city"
          control={form.control}
          render={({ field, fieldState }) => (
            <div className="flex flex-col">
              <Label style={labelStyle}>{t('drawer.address.city')}</Label>
              <TextInput
                {...field}
                value={field.value ?? ''}
                placeholder={t('drawer.address.cityPlaceholder')}
                maxLength={FORM_FIELD_LIMITS.city}
              />
              <FieldError message={fieldState.error?.message} />
            </div>
          )}
        />
        <Controller
          name="address.state"
          control={form.control}
          render={({ field, fieldState }) => (
            <div className="flex flex-col">
              <Label style={labelStyle}>{t('drawer.address.state')}</Label>
              <TextInput {...field} value={field.value ?? ''} placeholder="UF" maxLength={2} />
              <FieldError message={fieldState.error?.message} />
            </div>
          )}
        />
      </div>
    </FormSection>
  );
};
